import { useAuth } from "@stores/auth-store"
import { toast } from "@hooks/use-toast"
import { EDIT_USER_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { usePermissions } from "@pma/hooks/use-permissions"
import { cn } from "@pma/utils/helpers"
import { useMutation } from "@tanstack/react-query"
import axios from "axios"
import { parseCookies } from "nookies"
import { ReactNode, useState } from "react"
import { Edit2 } from "lucide-react"
import useTranslator from "@hooks/use-translator"
import DepartmentsSelector from "./departments-selector"
import ManagerSelector from "./manager-selector"
import JobTitleSelector from "./job-title-selector"
import RolesSelector from "./role-selector"
import { DictType } from "@pma/types"
import UserStatusEditor from "./user-status-editor"
import NameEditor from "./name-editor"
import PhoneEditor from "./phone-editor"

interface Props {
  id: string
  title: string
  children: ReactNode
  value: string | null | undefined
  refetch?: CallableFunction
  recaptchaToken?: any
  setV2Fallback?: any
  v2Fallback?: any
  task?: DictType
}
const UserEditor = ({
  children,
  title,
  value,
  refetch,
  id,
  recaptchaToken,
  setV2Fallback,
  v2Fallback,
  task,
}: Props) => {
  const { signIn, authConfig } = useAuth()
  const { translate, locale } = useTranslator()
  const { isOrgAdmin: isAdmin } = usePermissions()

  let Component
  const authUserId = authConfig?.id
  const isCurrentUser = authUserId === id
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const [hover, setHover] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [updatedValue, setUpdatedValue] = useState<any>(value ?? "")

  const updateUserDetails = async (v2Token?: string) => {
    if (!v2Fallback && !recaptchaToken.v3) return

    if ((v2Fallback && !v2Token) || !recaptchaToken.v3) return

    const payload = {
      ["selected_languag"]: locale,
      [title]: updatedValue,
      recaptcha_response: !v2Fallback ? recaptchaToken.v3 : undefined,
      recaptcha_response_v2: v2Token,
      recive_emails: task?.recive_emails,
      recive_whatsapp: task?.recive_whatsapp,
    }

    if (updatedValue?.trim() !== "") {
      const response = await axios.post(SERVER_URL + EDIT_USER_ENDPOINT, payload, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })

      return response.data
    }
  }

  const updateUserDetailsMutation = useMutation({
    mutationKey: [`${title}`, updatedValue],
    mutationFn: (token: string | undefined) => updateUserDetails(token),
    onSuccess: (data) => {
      if (isCurrentUser) {
        signIn({
          ...{
            [title]: updatedValue,
          },
        })
      }

      if (v2Fallback) setV2Fallback(false)

      refetch && refetch()
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      if (errorMessage === "Invalid Captcha Response. Please try again.") {
        setV2Fallback(true)
      } else {
        toast({
          title: translate("Error"),
          description: `${translate("Error occurred while updating")} ${title.replace("_", " ")}`,
          variant: "error",
        })
      }
    },
  })

  const onSubmit = (token?: string) => {
    if (updatedValue && updatedValue !== value) updateUserDetailsMutation.mutate(undefined)
    if (v2Fallback && token) updateUserDetailsMutation.mutate(token)
    setEditMode(false)
    return
  }

  switch (title) {
    case "phone_number":
      Component = (
        <PhoneEditor
          phoneNumber={value!}
          userId={id}
          refetch={refetch!}
          setEditMode={setEditMode}
        />
      )
      break
    case "department":
      Component = <DepartmentsSelector department={value!} userId={id} refetch={refetch!} />
      break
    case "role":
      Component = <RolesSelector role={value!} userId={id} refetch={refetch!} />
      break
    case "job_title":
      Component = <JobTitleSelector title={value!} userId={id} refetch={refetch!} />
      break
    case "manager":
      Component = <ManagerSelector managerId={value!} userId={id} refetch={refetch!} />
      break
    case "status":
      Component = <UserStatusEditor status={value!} userId={id} refetch={refetch!} />
      break
    case "name":
      Component = (
        <NameEditor userId={id} refetch={refetch!} name={value!} setEditMode={setEditMode} />
      )
      break
    default:
      Component = (
        <div className='flex flex-col'>
          <input
            className='rounded border bg-white/20 px-2 outline-none focus:border-primary'
            value={updatedValue}
            onChange={(e) => setUpdatedValue(e?.target?.value)}
            autoFocus
            onBlur={() => onSubmit()}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSubmit()
            }}
          />
        </div>
      )
  }

  if (!isCurrentUser && !isAdmin) return <>{children}</>

  return (
    <div
      className={cn(
        "flex w-fit cursor-pointer flex-col items-start gap-2 text-center font-semibold text-black",
        updateUserDetailsMutation.isPending &&
        "animate-shimmer bg-gradient-to-r from-black to-slate-500 bg-clip-text font-light text-transparent",
      )}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => setEditMode(true)}
    >
      {!editMode && (
        <div className='relative pe-2'>
          {hover && (
            <Edit2 className='hover:border-3 absolute -right-2 -top-2 text-primary' size={12} />
          )}
          {children}
        </div>
      )}
      {editMode && <>{Component}</>}
    </div>
  )
}

export default UserEditor
